import jsPDF from "jspdf";
import api from "./api";
import { getJobQrDataUrl } from "./qr";
import { describePart, LABOR_DESCRIPTION, INCLUDED_SERVICES, WARRANTY } from "./partDictionary";

const formatTL = (n) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(n || 0);
const fmt = (iso) => iso ? new Date(iso).toLocaleString("tr-TR") : "-";

let _fontLoaded = false;
let _fontBase64 = null;

async function ensureFont(pdf) {
  if (_fontBase64 == null) {
    try {
      const res = await fetch("/fonts/Roboto-Regular.ttf");
      const buf = await res.arrayBuffer();
      let binary = "";
      const bytes = new Uint8Array(buf);
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
      }
      _fontBase64 = btoa(binary);
    } catch (e) {
      console.error("Font yüklenemedi", e);
      _fontBase64 = "";
    }
  }
  if (_fontBase64) {
    pdf.addFileToVFS("Roboto-Regular.ttf", _fontBase64);
    pdf.addFont("Roboto-Regular.ttf", "Roboto", "normal");
    pdf.addFont("Roboto-Regular.ttf", "Roboto", "bold");
    pdf.setFont("Roboto", "normal");
    _fontLoaded = true;
    return true;
  }
  return false;
}

async function loadImageAsDataURL(url) {
  if (!url) return null;
  try {
    // Eğer logo direkt http ile geliyorsa ve CORS sorunu varsa, backend proxy üzerinden çek
    let fetchUrl = url;
    const isExternal = url.startsWith("http") && !url.startsWith(window.location.origin);
    if (isExternal && !url.includes("/api/files/")) {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      fetchUrl = `${backend}/api/proxy-image?url=${encodeURIComponent(url)}`;
    }
    const res = await fetch(fetchUrl, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn("loadImage failed for", url, e);
    return null;
  }
}

function detectImageType(dataUrl) {
  if (!dataUrl) return "PNG";
  if (dataUrl.startsWith("data:image/jpeg") || dataUrl.startsWith("data:image/jpg")) return "JPEG";
  if (dataUrl.startsWith("data:image/webp")) return "WEBP";
  return "PNG";
}

async function loadFooterLogo() {
  try {
    const res = await fetch("/brand/footer-logo.png");
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

// Servis fişi tasarım ayarları. Varsayılanlar mevcut PDF çıktısıyla birebir aynıdır.
export const DEFAULT_PDF_DESIGN = {
  title: "SERVİS FİŞİ",
  title_color: "#0f172a",
  show_brand_logo: true,
  show_parts: true,
  show_job_note: true,
  show_default_note: true,
  show_signatures: true,
  show_footer_logo: true,
  show_dates: true,
  customer_label: "MÜŞTERİ",
  device_label: "CİHAZ",
  fault_label: "ARIZA TANIMI",
  work_label: "YAPILAN İŞLEMLER",
  parts_label: "KULLANILAN/DEĞİŞEN PARÇALAR",
  notes_label: "ÖNEMLİ NOTLAR",
  customer_sig_label: "MÜŞTERİ İMZASI",
  tech_sig_label: "TEKNİSYEN İMZASI",
  footer_text: "",
};

const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return [15, 23, 42];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

export function mergePdfDesign(d) {
  const out = { ...DEFAULT_PDF_DESIGN };
  Object.keys(d || {}).forEach((k) => {
    if (d[k] !== undefined && d[k] !== null && (typeof d[k] !== "string" || k === "footer_text" || d[k].trim() !== "")) out[k] = d[k];
  });
  return out;
}

export async function generateServicePDF(job, opts = {}) {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  let settings = {};
  try {
    const res = await api.get("/settings");
    settings = res.data || {};
  } catch (e) {
    // settings yoksa devam et
  }
  const D = mergePdfDesign(opts.design || settings.pdf_design);
  const W = 210;
  let y = 12;

  const fontOk = await ensureFont(pdf);
  const FONT = fontOk ? "Roboto" : "helvetica";

  // ---------- Header ----------
  // Brand logo (auto from device) — aspect ratio'yu koru
  if (D.show_brand_logo && job.device_brand_logo) {
    const logoData = await loadImageAsDataURL(job.device_brand_logo);
    if (logoData) {
      try {
        const img = new Image();
        await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = logoData; });
        const maxW = 32, maxH = 20;
        const ratio = img.width / img.height || 1;
        let w = maxW, h = maxW / ratio;
        if (h > maxH) { h = maxH; w = maxH * ratio; }
        pdf.addImage(logoData, detectImageType(logoData), 12, y, w, h);
      } catch {
        // fallback
        try { pdf.addImage(logoData, detectImageType(logoData), 12, y, 28, 18); } catch {}
      }
    }
  }
  pdf.setFont(FONT, "bold"); pdf.setFontSize(20); pdf.setTextColor(...hexToRgb(D.title_color));
  pdf.text(D.title, W - 12, y + 8, { align: "right" });
  pdf.setFont(FONT, "normal"); pdf.setFontSize(9); pdf.setTextColor(100);
  pdf.text(job.device_brand_name || "—", W - 12, y + 14, { align: "right" });
  pdf.text(`No: ${job.job_number}`, W - 12, y + 19, { align: "right" });

  y += 26;
  pdf.setDrawColor(220); pdf.line(12, y, W - 12, y); y += 6;

  // ---------- Customer + Device ----------
  pdf.setFont(FONT, "bold"); pdf.setFontSize(8); pdf.setTextColor(80);
  pdf.text(D.customer_label, 12, y);
  pdf.text(D.device_label, W / 2 + 4, y);
  y += 4;
  pdf.setFont(FONT, "normal"); pdf.setFontSize(10); pdf.setTextColor(20);
  pdf.text(job.customer_name || "-", 12, y);
  pdf.text(`${job.device_brand_name || ""} ${job.device_model || ""}`.trim() || "-", W / 2 + 4, y);
  y += 5;
  pdf.setFontSize(9); pdf.setTextColor(80);
  pdf.text(job.customer_phone || "", 12, y);
  pdf.text(`Tip: ${job.device_type || "-"}`, W / 2 + 4, y);
  y += 5;
  if (job.customer_address) {
    const addrLines = pdf.splitTextToSize(job.customer_address, 90);
    pdf.text(addrLines, 12, y);
    y += addrLines.length * 4;
  } else {
    y += 4;
  }
  pdf.text(`Seri: ${job.device_serial || "-"}`, W / 2 + 4, y - 4);
  y += 6;

  pdf.setDrawColor(220); pdf.line(12, y, W - 12, y); y += 6;

  // ---------- Fault ----------
  pdf.setFont(FONT, "bold"); pdf.setFontSize(8); pdf.setTextColor(80);
  pdf.text(D.fault_label, 12, y); y += 4;
  pdf.setFont(FONT, "normal"); pdf.setFontSize(10); pdf.setTextColor(20);
  const faultLines = pdf.splitTextToSize(job.fault_description || "-", W - 24);
  pdf.text(faultLines, 12, y); y += faultLines.length * 5 + 4;

  // ---------- Work done ----------
  pdf.setFont(FONT, "bold"); pdf.setFontSize(8); pdf.setTextColor(80);
  pdf.text(D.work_label, 12, y); y += 4;
  pdf.setFont(FONT, "normal"); pdf.setFontSize(10); pdf.setTextColor(20);
  const workLines = pdf.splitTextToSize(job.work_done || "-", W - 24);
  pdf.text(workLines, 12, y); y += workLines.length * 5 + 6;

  // ---------- Admin Notu ----------
  if (D.show_job_note && job.pdf_note) {
    pdf.setFont(FONT, "bold"); pdf.setFontSize(8); pdf.setTextColor(80);
    pdf.text("NOT", 12, y); y += 4;
    pdf.setFont(FONT, "normal"); pdf.setFontSize(10); pdf.setTextColor(20);
    const noteLines = pdf.splitTextToSize(job.pdf_note, W - 24);
    pdf.text(noteLines, 12, y); y += noteLines.length * 5 + 6;
  }

  // ---------- Parts table ----------
  if (D.show_parts && job.parts_used && job.parts_used.length) {
    pdf.setFont(FONT, "bold"); pdf.setFontSize(8); pdf.setTextColor(80);
    pdf.text(D.parts_label, 12, y); y += 4;
    pdf.setFillColor(245); pdf.rect(12, y, W - 24, 6, "F");
    pdf.setFontSize(9); pdf.setTextColor(40);
    pdf.text("Parça", 14, y + 4);
    pdf.text("Adet", 130, y + 4, { align: "right" });
    pdf.text("Birim", 160, y + 4, { align: "right" });
    pdf.text("Toplam", W - 14, y + 4, { align: "right" });
    y += 6;
    pdf.setFont(FONT, "normal");
    job.parts_used.forEach(p => {
      const nameLines = pdf.splitTextToSize(String(p.name || "-"), 110);
      pdf.text(nameLines, 14, y + 4);
      pdf.text(String(p.qty || 0), 130, y + 4, { align: "right" });
      pdf.text(formatTL(p.unit_price), 160, y + 4, { align: "right" });
      pdf.text(formatTL(p.total), W - 14, y + 4, { align: "right" });
      y += Math.max(6, nameLines.length * 5 + 1);
      pdf.setDrawColor(240); pdf.line(12, y, W - 12, y);
    });
    y += 4;
  }

  // ---------- Totals ----------
  const partsTotal = (job.parts_used || []).reduce((s, p) => s + (Number(p.total) || 0), 0);
  const labor = Number(job.labor_fee) || 0;
  const total = partsTotal + labor;
  pdf.setDrawColor(200); pdf.line(120, y, W - 12, y); y += 5;
  pdf.setFont(FONT, "normal"); pdf.setFontSize(9); pdf.setTextColor(80);
  pdf.text("Parça Toplam:", 130, y); pdf.text(formatTL(partsTotal), W - 14, y, { align: "right" }); y += 5;
  pdf.text("İşçilik:", 130, y); pdf.text(formatTL(labor), W - 14, y, { align: "right" }); y += 5;
  pdf.setFont(FONT, "bold"); pdf.setFontSize(11); pdf.setTextColor(20);
  pdf.text("GENEL TOPLAM:", 130, y); pdf.text(formatTL(total), W - 14, y, { align: "right" }); y += 10;

  // ---------- Otomatik PDF Notu (Ayarlar) — imzaların üstünde ----------
  if (D.show_default_note && settings.default_pdf_note) {
    if (y > 220) { pdf.addPage(); y = 20; }
    pdf.setDrawColor(220); pdf.line(12, y, W - 12, y); y += 6;
    pdf.setFont(FONT, "bold"); pdf.setFontSize(8); pdf.setTextColor(80);
    pdf.text(D.notes_label, 12, y); y += 4;
    pdf.setFont(FONT, "normal"); pdf.setFontSize(9); pdf.setTextColor(40);
    const settingsNoteLines = pdf.splitTextToSize(settings.default_pdf_note, W - 24);
    pdf.text(settingsNoteLines, 12, y);
    y += settingsNoteLines.length * 4.5 + 4;
  }

  // ---------- Signatures ----------
  if (D.show_signatures) {
  if (y > 230) { pdf.addPage(); y = 20; }
  pdf.setDrawColor(220); pdf.line(12, y, W - 12, y); y += 6;
  pdf.setFont(FONT, "bold"); pdf.setFontSize(8); pdf.setTextColor(80);
  pdf.text(D.customer_sig_label, 12, y);
  pdf.text(D.tech_sig_label, W / 2 + 4, y);
  y += 3;

  // Customer signature
  if (job.signature_url) {
    const sigData = await loadImageAsDataURL(job.signature_url);
    if (sigData) {
      try { pdf.addImage(sigData, detectImageType(sigData), 12, y, 70, 25); } catch {}
    }
  }
  // Tech signature
  if (job.tech_signature_url) {
    const techSigData = await loadImageAsDataURL(job.tech_signature_url);
    if (techSigData) {
      try { pdf.addImage(techSigData, detectImageType(techSigData), W / 2 + 4, y, 70, 25); } catch {}
    }
  }
  y += 28;
  pdf.setFont(FONT, "normal"); pdf.setFontSize(8); pdf.setTextColor(120);
  pdf.text(job.customer_name || "", 12, y);
  pdf.text(job.assigned_technician_name || "", W / 2 + 4, y);
  y += 8;
  }

  // ---------- Cihaz QR kodu (servis geçmişi / garanti / yeni talep) ----------
  if (job.id && opts.qr !== false) {
    try {
      const qrData = await getJobQrDataUrl(job.id);
      if (qrData) {
        if (y > 238) { pdf.addPage(); y = 20; }
        pdf.setDrawColor(220); pdf.roundedRect(12, y, W - 24, 28, 2, 2);
        pdf.addImage(qrData, "PNG", 15, y + 2, 24, 24);
        pdf.setFont(FONT, "bold"); pdf.setFontSize(9); pdf.setTextColor(30);
        pdf.text("Cihazınızın QR kodu", 44, y + 8);
        pdf.setFont(FONT, "normal"); pdf.setFontSize(8); pdf.setTextColor(90);
        pdf.text(pdf.splitTextToSize("Telefonunuzun kamerasıyla okutarak cihazınızın servis geçmişini ve garanti durumunu görebilir, tek dokunuşla yeni servis talebi oluşturabilirsiniz.", W - 62), 44, y + 13);
        y += 32;
      }
    } catch (e) { /* QR alınamazsa form yine de oluşur */ }
  }

  // ---------- Footer ----------
  pdf.setFontSize(7); pdf.setTextColor(150);
  // ---------- Footer with ServisPlus logo ----------
  const footerLogo = D.show_footer_logo ? await loadFooterLogo() : null;
  if (footerLogo) {
    try {
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = footerLogo; });
      const maxW = 40, maxH = 14;
      const ratio = img.width / img.height || 1;
      let w = maxW, h = maxW / ratio;
      if (h > maxH) { h = maxH; w = maxH * ratio; }
      pdf.addImage(footerLogo, "PNG", (W - w) / 2, 270, w, h);
    } catch {}
  }
  pdf.setFontSize(7); pdf.setTextColor(150);
  if (D.show_dates) pdf.text(`Oluşturma: ${fmt(job.created_at)}  •  Tamamlanma: ${fmt(job.completed_at)}`, W / 2, 287, { align: "center" });
  if (D.footer_text) { pdf.setFontSize(8); pdf.text(D.footer_text, W / 2, D.show_dates ? 283 : 287, { align: "center" }); }

  if (opts.preview) return pdf.output("blob");
  pdf.save(`${job.job_number}-servis-fisi.pdf`);
}

// ============================================================
//                  FİYAT TEKLİFİ PDF
// ============================================================
export async function generateQuotePDF(quote, options = {}) {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const H = 297;
  let y = 14;

  const fontOk = await ensureFont(pdf);
  const FONT = fontOk ? "Roboto" : "helvetica";
  const ensureSpace = (need) => {
    if (y + need > H - 20) {
      pdf.addPage();
      y = 18;
    }
  };

  // ----- Şirket / Header -----
  const footerLogo = await loadFooterLogo();
  if (footerLogo) {
    try {
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = footerLogo; });
      const maxW = 38, maxH = 22;
      const ratio = img.width / img.height || 1;
      let w = maxW, h = maxW / ratio;
      if (h > maxH) { h = maxH; w = maxH * ratio; }
      pdf.addImage(footerLogo, "PNG", 12, y, w, h);
    } catch {}
  }
  pdf.setFont(FONT, "bold"); pdf.setFontSize(22); pdf.setTextColor(15, 23, 42);
  pdf.text("FİYAT TEKLİFİ", W - 12, y + 9, { align: "right" });
  pdf.setFont(FONT, "normal"); pdf.setFontSize(9); pdf.setTextColor(110);
  pdf.text(`Teklif No: ${quote.quote_number || "-"}`, W - 12, y + 16, { align: "right" });
  pdf.text(`Tarih: ${fmt(quote.created_at)}`, W - 12, y + 21, { align: "right" });
  y += 30;

  pdf.setDrawColor(220); pdf.line(12, y, W - 12, y); y += 6;

  // ----- Müşteri + Teknisyen -----
  pdf.setFont(FONT, "bold"); pdf.setFontSize(8); pdf.setTextColor(80);
  pdf.text("MÜŞTERİ", 12, y);
  pdf.text("TEKNİSYEN", W / 2 + 4, y);
  y += 4;
  pdf.setFont(FONT, "normal"); pdf.setFontSize(11); pdf.setTextColor(20);
  pdf.text(quote.customer_name || "-", 12, y);
  pdf.text(quote.technician_name || "-", W / 2 + 4, y);
  y += 5;
  pdf.setFontSize(9); pdf.setTextColor(100);
  pdf.text(quote.customer_phone || "", 12, y); y += 6;

  // ----- Cihaz Bilgileri -----
  const hasDevice = quote.device_brand_name || quote.device_model || quote.device_serial || quote.device_type;
  if (hasDevice) {
    pdf.setDrawColor(220); pdf.line(12, y, W - 12, y); y += 6;
    pdf.setFont(FONT, "bold"); pdf.setFontSize(8); pdf.setTextColor(80);
    pdf.text("CİHAZ BİLGİLERİ", 12, y); y += 5;
    pdf.setFont(FONT, "normal"); pdf.setFontSize(10); pdf.setTextColor(20);
    const devLine1 = [];
    if (quote.device_brand_name) devLine1.push(`Marka: ${quote.device_brand_name}`);
    if (quote.device_type) devLine1.push(`Tip: ${quote.device_type}`);
    const devLine2 = [];
    if (quote.device_model) devLine2.push(`Model: ${quote.device_model}`);
    if (quote.device_serial) devLine2.push(`Seri No: ${quote.device_serial}`);
    if (devLine1.length) { pdf.text(devLine1.join("    "), 12, y); y += 5; }
    if (devLine2.length) { pdf.text(devLine2.join("    "), 12, y); y += 5; }
    y += 2;
  }

  pdf.setDrawColor(220); pdf.line(12, y, W - 12, y); y += 6;

  // ----- Arıza -----
  pdf.setFont(FONT, "bold"); pdf.setFontSize(8); pdf.setTextColor(80);
  pdf.text("ARIZA / İŞ TANIMI", 12, y); y += 5;
  pdf.setFont(FONT, "normal"); pdf.setFontSize(11); pdf.setTextColor(20);
  const faultLines = pdf.splitTextToSize(quote.fault_description || "-", W - 24);
  pdf.text(faultLines, 12, y); y += faultLines.length * 5 + 4;

  // ----- Kalemler Tablosu -----
  ensureSpace(20);
  pdf.setFont(FONT, "bold"); pdf.setFontSize(8); pdf.setTextColor(80);
  pdf.text("İŞLEM KALEMLERİ", 12, y); y += 4;
  pdf.setFillColor(15, 23, 42); pdf.rect(12, y, W - 24, 8, "F");
  pdf.setFont(FONT, "bold"); pdf.setFontSize(10); pdf.setTextColor(255);
  pdf.text("Açıklama", 14, y + 5.5);
  pdf.text("Tutar (₺)", W - 14, y + 5.5, { align: "right" });
  y += 8;
  pdf.setFont(FONT, "normal");

  const renderRow = (title, amount, desc) => {
    const descLines = pdf.splitTextToSize(desc, W - 30);
    ensureSpace(descLines.length * 4 + 10);
    pdf.setFont(FONT, "bold"); pdf.setFontSize(11); pdf.setTextColor(20);
    pdf.text(title, 14, y + 5);
    pdf.text(formatTL(amount), W - 14, y + 5, { align: "right" });
    y += 7;
    pdf.setFont(FONT, "normal"); pdf.setFontSize(8); pdf.setTextColor(110);
    pdf.text(descLines, 14, y + 3);
    y += descLines.length * 4 + 3;
    pdf.setDrawColor(232); pdf.line(12, y, W - 12, y); y += 1;
  };

  if (Number(quote.labor_fee) > 0) {
    renderRow("İşçilik Ücreti", quote.labor_fee, LABOR_DESCRIPTION);
  }
  (quote.parts || []).forEach(p => {
    renderRow(p.name || "-", p.amount, describePart(p.name));
  });
  y += 4;

  // ----- DAHİL Hizmetler -----
  ensureSpace(30);
  pdf.setFillColor(236, 253, 245);
  pdf.setDrawColor(167, 243, 208);
  const incH = INCLUDED_SERVICES.length * 5 + 12;
  pdf.roundedRect(12, y, W - 24, incH, 2, 2, "FD");
  pdf.setFont(FONT, "bold"); pdf.setFontSize(9); pdf.setTextColor(6, 95, 70);
  pdf.text("BU FİYATA DAHİL OLAN HİZMETLER", 16, y + 6);
  pdf.setFont(FONT, "normal"); pdf.setFontSize(9); pdf.setTextColor(20, 83, 45);
  INCLUDED_SERVICES.forEach((s, i) => {
    pdf.text(`✓  ${s}`, 16, y + 12 + i * 5);
  });
  y += incH + 6;

  // ----- Garanti -----
  ensureSpace(20);
  const halfW = (W - 24 - 4) / 2;
  pdf.setFillColor(15, 23, 42);
  pdf.roundedRect(12, y, halfW, 16, 2, 2, "F");
  pdf.roundedRect(12 + halfW + 4, y, halfW, 16, 2, 2, "F");
  pdf.setFont(FONT, "normal"); pdf.setFontSize(8); pdf.setTextColor(148, 163, 184);
  pdf.text("PARÇA", 16, y + 6);
  pdf.text("İŞÇİLİK", 16 + halfW + 4, y + 6);
  pdf.setFont(FONT, "bold"); pdf.setFontSize(11); pdf.setTextColor(255);
  pdf.text(WARRANTY.parts, 16, y + 12);
  pdf.text(WARRANTY.labor, 16 + halfW + 4, y + 12);
  y += 22;

  // ----- Toplam -----
  ensureSpace(20);
  pdf.setFillColor(15, 23, 42);
  pdf.roundedRect(12, y, W - 24, 18, 2, 2, "F");
  pdf.setFont(FONT, "bold"); pdf.setFontSize(11); pdf.setTextColor(148, 163, 184);
  pdf.text("GENEL TOPLAM", 16, y + 8);
  pdf.setFontSize(9); pdf.setTextColor(120);
  pdf.text("Garantili Servis Paketi", 16, y + 14);
  pdf.setFont(FONT, "bold"); pdf.setFontSize(20); pdf.setTextColor(255);
  pdf.text(formatTL(quote.target_total), W - 16, y + 12.5, { align: "right" });
  y += 24;

  // ----- Footer not (indirim için yöneticiye iletişim) -----
  const footerNote = quote.footer_note || options.fallbackFooter || "";
  if (footerNote) {
    ensureSpace(20);
    pdf.setFillColor(254, 243, 199);
    pdf.setDrawColor(252, 211, 77);
    const noteLines = pdf.splitTextToSize(footerNote, W - 32);
    const noteH = noteLines.length * 5 + 8;
    pdf.roundedRect(12, y, W - 24, noteH, 2, 2, "FD");
    pdf.setFont(FONT, "normal"); pdf.setFontSize(10); pdf.setTextColor(120, 53, 15);
    pdf.text(noteLines, 16, y + 6);
    y += noteH + 6;
  }

  // ----- Bottom info -----
  pdf.setFont(FONT, "normal"); pdf.setFontSize(8); pdf.setTextColor(140);
  pdf.text("Bu teklif yalnızca bilgilendirme amaçlıdır. Onaylandığında servis kaydına dönüştürülür.", W / 2, H - 12, { align: "center" });
  pdf.text("ServisPlus", W / 2, H - 6, { align: "center" });

  return pdf;
}

export async function downloadQuotePDF(quote, options) {
  const pdf = await generateQuotePDF(quote, options);
  pdf.save(`${quote.quote_number || "teklif"}-fiyat-teklifi.pdf`);
}

export async function quotePDFBlob(quote, options) {
  const pdf = await generateQuotePDF(quote, options);
  return pdf.output("blob");
}
